// frontend/src/ReleaseNotes/releaseNotesPublicSlice.ts
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { IImpact, IModule, IReleaseType } from "@multiconsult-group/multiplacer-core";
import { IRootState } from "../app/store";

export type IReleaseNotesPublicFiltersState = {
  selectedModules: IModule[];
  selectedTypes: IReleaseType[];
  selectedImpacts: IImpact[];
};

const initialState: IReleaseNotesPublicFiltersState = {
  selectedModules: [],
  selectedTypes: [],
  selectedImpacts: [],
};

const toggleInArray = <T,>(arr: T[], value: T): T[] => {
  return arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
};

export const releaseNotesPublicSlice = createSlice({
  name: "releaseNotesPublic",
  initialState,
  reducers: {
    // -------------------- MODULES --------------------
    setReleaseNotesSelectedModulesInRedux: (state, action: PayloadAction<IModule[]>) => {
      state.selectedModules = action.payload;
    },
    toggleReleaseNotesSelectedModuleInRedux: (state, action: PayloadAction<IModule>) => {
      state.selectedModules = toggleInArray(state.selectedModules, action.payload);
    },

    // -------------------- TYPES --------------------
    setReleaseNotesSelectedTypesInRedux: (state, action: PayloadAction<IReleaseType[]>) => {
      state.selectedTypes = action.payload;
    },
    toggleReleaseNotesSelectedTypeInRedux: (state, action: PayloadAction<IReleaseType>) => {
      state.selectedTypes = toggleInArray(state.selectedTypes, action.payload);
    },

    // -------------------- IMPACTS --------------------
    setReleaseNotesSelectedImpactsInRedux: (state, action: PayloadAction<IImpact[]>) => {
      state.selectedImpacts = action.payload;
    },
    toggleReleaseNotesSelectedImpactInRedux: (state, action: PayloadAction<IImpact>) => {
      state.selectedImpacts = toggleInArray(state.selectedImpacts, action.payload);
    },

    // -------------------- BULK --------------------
    clearReleaseNotesAllFiltersInRedux: (state) => {
      state.selectedModules = [];
      state.selectedTypes = [];
      state.selectedImpacts = [];
    },

    setReleaseNotesAllFiltersInRedux: (
      state,
      action: PayloadAction<{
        selectedModules?: IModule[];
        selectedTypes?: IReleaseType[];
        selectedImpacts?: IImpact[];
      }>,
    ) => {
      if (action.payload.selectedModules) state.selectedModules = action.payload.selectedModules;
      if (action.payload.selectedTypes) state.selectedTypes = action.payload.selectedTypes;
      if (action.payload.selectedImpacts) state.selectedImpacts = action.payload.selectedImpacts;
    },
  },
});

export const {
  setReleaseNotesSelectedModulesInRedux,
  toggleReleaseNotesSelectedModuleInRedux,
  setReleaseNotesSelectedTypesInRedux,
  toggleReleaseNotesSelectedTypeInRedux,
  setReleaseNotesSelectedImpactsInRedux,
  toggleReleaseNotesSelectedImpactInRedux,
  clearReleaseNotesAllFiltersInRedux,
  setReleaseNotesAllFiltersInRedux,
} = releaseNotesPublicSlice.actions;

export const releaseNotesPublicReducer = releaseNotesPublicSlice.reducer;

/* -------------------------------- selectors ------------------------------- */

export const selectReleaseNotesPublicFilters = (s: IRootState) => s.releaseNotesPublic;

export const selectReleaseNotesSelectedModules = (s: IRootState) => s.releaseNotesPublic.selectedModules;
export const selectReleaseNotesSelectedTypes = (s: IRootState) => s.releaseNotesPublic.selectedTypes;
export const selectReleaseNotesSelectedImpacts = (s: IRootState) => s.releaseNotesPublic.selectedImpacts;

export const selectReleaseNotesHasAnyFilter = (s: IRootState) => {
  const f = s.releaseNotesPublic;
  return f.selectedModules.length > 0 || f.selectedTypes.length > 0 || f.selectedImpacts.length > 0;
};
